import { useCallback, useEffect, useRef, useState } from "react"

export type UserLocation = {
  latitude: number
  longitude: number
}

export function useUserLocation(enabled: boolean) {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null)
  const isLocatingRef = useRef(false)
  const hasRequestedRef = useRef(false)

  const applyUserPosition = useCallback((position: GeolocationPosition) => {
    setUserLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    })
    isLocatingRef.current = false
  }, [])

  const applyIpLocation = useCallback(async () => {
    try {
      const response = await fetch("https://ipwho.is/")
      const data = await response.json()
      if (
        data &&
        data.success !== false &&
        typeof data.latitude === "number" &&
        typeof data.longitude === "number"
      ) {
        setUserLocation({
          latitude: data.latitude,
          longitude: data.longitude,
        })
      }
    } catch {
    } finally {
      isLocatingRef.current = false
    }
  }, [])

  const requestUserLocation = useCallback(() => {
    if (isLocatingRef.current) return

    isLocatingRef.current = true

    if (!navigator.geolocation || !window.isSecureContext) {
      void applyIpLocation()
      return
    }

    const options: PositionOptions = {
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 600000,
    }

    navigator.geolocation.getCurrentPosition(
      applyUserPosition,
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          isLocatingRef.current = false
          return
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          window.setTimeout(() => {
            navigator.geolocation.getCurrentPosition(
              applyUserPosition,
              () => {
                void applyIpLocation()
              },
              { ...options, maximumAge: Infinity },
            )
          }, 3000)
          return
        }
        void applyIpLocation()
      },
      options,
    )
  }, [applyIpLocation, applyUserPosition])

  useEffect(() => {
    if (!enabled) {
      hasRequestedRef.current = false
      isLocatingRef.current = false
      setUserLocation(null)
      return
    }

    if (hasRequestedRef.current) return
    hasRequestedRef.current = true
    requestUserLocation()
  }, [enabled, requestUserLocation])

  return userLocation
}
